import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component } from '@angular/core';
import { finalize } from 'rxjs/operators';

import { InviteFriendEmail } from 'AccountsApp/ts/interfaces/invite-friend-email.interface';

import { AccountsService } from 'AccountsApp/ts/services/accounts.service';

/**
 *
 * Invite Friend Modal Component
 *
 * This component is responsible for:
 *  1. Accepting user input of the email addresses for friends they wish to invite to YCharts
 *  2. Handling when the Send button is clicked
 *
 */

@Component({
    standalone: false,
    selector: 'ycn-invite-friend-modal',
    templateUrl: './invite-friend-modal.template.html',
})
export class InviteFriendModalComponent {
    allEmailsValid: boolean = true;

    emails: InviteFriendEmail[] = [];

    emailInput: string = '';

    modalOpen: boolean = false;

    processingRequest: boolean = false;

    showAddEmail: boolean = true;

    showCompletionModal: boolean = false;

    showLimitReached: boolean = false;

    MAX_NUM_EMAILS: number = 10;

    constructor(private accountsService: AccountsService, private cdr: ChangeDetectorRef) {
        this.reset();
    }

    openModal(): void {
        this.modalOpen = true;
    }

    closeModal(): void {
        /**
         * Closes the modal and resets to the default state.
         */
        this.modalOpen = false;
        this.reset();
    }

    submitReferral(): void {
        /**
         * POSTs the emails to the backend view. Depending on whether the backend returns a success or error response,
         * we show various messages to the user.
         */
        this.processingRequest = true;
        const emailValues = this.emails.filter((email) => !!email.value).map((email) => email.value.trim());

        const combinedEmails = `${emailValues.join()},`;
        this.accountsService
            .postReferFriends(combinedEmails)
            .pipe(
                // Finalize gets called after both `next` and after `error`
                finalize(() => {
                    this.processingRequest = false;
                })
            )
            .subscribe({
                next: (res: HttpResponse<object>) => {
                    if (res.status === 200) {
                        this.showCompletionModal = true;
                    }
                },
                error: (error: HttpErrorResponse) => {
                    if (error.status === 400) {
                        const invalidEmails = error.error.invalid_emails;
                        for (let i = 0; i < this.emails.length; i += 1) {
                            this.emails[i].isValid = !invalidEmails.includes(this.emails[i].value);
                        }
                    } else if (error.status === 403) {
                        if (error.error.over_email_limit) {
                            this.showLimitReached = true;
                        }
                    }
                },
                complete: () => {
                    // Parent component uses OnPush change detection, so manually trigger change detection here.
                    this.cdr.detectChanges();
                },
            });
    }

    onInputKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.emailInput !== '') this.addEmail(this.emailInput);
        }
    }

    onInputBlur(): void {
        if (this.emailInput.trim() !== '') this.addEmail(this.emailInput);
    }

    validateEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    addEmail(val: string): void {
        /**
         * As long as we have not hit MAX_NUM_EMAILS, add an empty email to the array.
         */
        const newEmail: InviteFriendEmail = {
            value: val.trim(),
            isValid: this.validateEmail(val),
        };
        this.emails.push(newEmail);
        if (this.emails.length >= this.MAX_NUM_EMAILS) {
            this.showAddEmail = false;
        }
        if (!newEmail.isValid) {
            this.allEmailsValid = false;
        }
        this.emailInput = '';
    }

    removeEmail(index: number): void {
        /**
         * Removes an email from the array.
         */
        this.emails.splice(index, 1);
        this.allEmailsValid = this.emails.every((email) => email.isValid);
        if (this.emails.length < this.MAX_NUM_EMAILS) {
            this.showAddEmail = true;
        }
    }

    reset(): void {
        /**
         * Resets all variables to their default state. This is called whenever the modal is closed, meaning we will
         * always start with a fresh slate each time the modal is opened.
         */
        this.showLimitReached = false;
        this.showCompletionModal = false;
        this.showAddEmail = true;
        this.processingRequest = false;
        this.emailInput = '';
        this.emails = [];
    }
}
